import { useMemo } from 'react';

import { useGetAllCameras, useGetInventoriesByTypeCrossSite } from '@restDataAccess';
import { useTranslation } from 'react-i18next';

import { useInventoryDataCtx } from '@/providers/appCoreProvider/accessibleSitesProvider/siteProvider/inventoryDataProvider/InventoryDataContext.ts';
import { useSiteContext } from '@/providers/appCoreProvider/accessibleSitesProvider/siteProvider/SiteContext.ts';

import type { UseInventorySiteSectionsOptions } from '../inventorySiteSections.types.ts';
import {
  buildAccessibleSiteSections,
  buildInventorySiteSections,
} from './useInventorySiteSections/inventorySiteSections.functions.ts';

/**
 * Cross-site inventory sections for vertical site tabs. Fetches by source (`cameras`, `types`, or
 * accessible sites from context), then groups inventories per site with optional parent/type buckets.
 *
 * Parent-group labels are resolved from the flat `id → name` map on
 * {@link useInventoryDataCtx} (`inventoryNameById`), keyed purely by `parentId` — so a camera's
 * parent, whatever its type (pen, barge, site root) and whichever site it lives on (cross-site in
 * portable mode, local site on edge), is named instead of falling back to "Not available".
 */
export const useInventorySiteSections = ({
  source,
  groupBy = 'none',
}: UseInventorySiteSectionsOptions) => {
  const { t } = useTranslation();
  const { sites } = useSiteContext();
  const notAvailableLabel = t('notAvailable');
  const fetchCameras = source.kind === 'cameras';
  const needsParentInventory = groupBy === 'parent';
  const { data: cameras, isLoading: camerasLoading } = useGetAllCameras(fetchCameras);
  const inventoryTypes = source.kind === 'types' && source.types.length ? source.types : undefined;
  const { data: typeQueryInventories, isLoading: typesQueryLoading } =
    useGetInventoriesByTypeCrossSite(inventoryTypes);
  const { inventoryNameById, isInventoryNamesLoading } = useInventoryDataCtx();

  const siteSections = useMemo(() => {
    if (source.kind === 'accessibleSites') return buildAccessibleSiteSections(sites);

    const inventories = source.kind === 'cameras' ? cameras : typeQueryInventories;
    if (!inventories?.length) return [];

    return buildInventorySiteSections(inventories, {
      notAvailableLabel,
      groupBy,
      sites,
      inventoryNameById: needsParentInventory ? inventoryNameById : undefined,
    });
  }, [
    cameras,
    groupBy,
    inventoryNameById,
    needsParentInventory,
    notAvailableLabel,
    sites,
    source.kind,
    typeQueryInventories,
  ]);

  // Cross-site cameras come from a cloud aggregation; parent groups resolve names from the mode-aware
  // `getNames` map (`inventoryNameById`). That fetch must not gate the camera picker — when names are
  // still loading or a parent is missing, grouping is best-effort and falls back to notAvailable.
  const isLoading =
    (source.kind === 'cameras' && camerasLoading) ||
    (source.kind === 'types' &&
      (typesQueryLoading || (needsParentInventory && isInventoryNamesLoading)));

  const isEmpty =
    source.kind === 'accessibleSites'
      ? !sites?.length
      : source.kind === 'cameras'
        ? !isLoading && !cameras?.length
        : !isLoading && !typeQueryInventories?.length;

  return { siteSections, isLoading, isEmpty };
};
